import { ActionIcon, Badge, Code, Group, Stack, Text, Tooltip } from '@mantine/core';
import { IconCopy } from '@tabler/icons-react';
import type { TimelineEvent } from '../api/cases';

interface Props {
  events: TimelineEvent[];
}

export function badgeColor(eventType: string): string {
  if (eventType.endsWith('.failed') || eventType.endsWith('.declined')) return 'red';
  if (eventType.endsWith('.completed')) return 'green';
  return 'gray';
}

function formatRelative(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 60_000) return 'just now';
  const mins = Math.floor(ms / 60_000);
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

async function copyToClipboard(text: string): Promise<void> {
  try {
    await navigator.clipboard?.writeText(text);
  } catch {
    // Best-effort. Older browsers / locked-down jsdom may reject; ignore.
  }
}

export function CaseTimeline({ events }: Props) {
  if (events.length === 0) {
    return <Text c="dimmed">No timeline events yet for this case.</Text>;
  }

  return (
    <Stack gap="sm">
      {events.map((e) => (
        <Group key={e.id} align="flex-start" gap="md" wrap="nowrap">
          <Tooltip label={e.timestamp} position="top">
            <Text size="xs" c="dimmed" w={90} style={{ flexShrink: 0 }}>
              {formatRelative(e.timestamp)}
            </Text>
          </Tooltip>
          <Stack gap={4} style={{ flex: 1 }}>
            <Group gap="xs">
              <Badge variant="light" color={badgeColor(e.event_type)} size="sm">
                {e.event_type}
              </Badge>
              {e.actor?.display_name && (
                <Text size="xs" c="dimmed">
                  {e.actor.display_name}
                  {e.actor.product_code ? ` · ${e.actor.product_code}` : ''}
                </Text>
              )}
            </Group>
            <Text size="sm">{e.summary}</Text>
            {e.correlation_id && (
              <Group gap={4} align="center">
                <Code style={{ fontSize: 11 }}>{e.correlation_id}</Code>
                <Tooltip label="Copy correlation id">
                  <ActionIcon
                    variant="subtle"
                    size="xs"
                    aria-label="Copy correlation id"
                    onClick={() => copyToClipboard(e.correlation_id!)}
                  >
                    <IconCopy size={12} />
                  </ActionIcon>
                </Tooltip>
              </Group>
            )}
          </Stack>
        </Group>
      ))}
    </Stack>
  );
}
